import type { AnalyticsSchemaColumnDto, AnalyticsSchemaObjectDto } from '@/shared/api';

/**
 * Runtime model of the reporting API, built from GET /api/analytics/schema.
 * Every table and view the backend maps gets list pages, filters and formatting automatically;
 * entity pages and cross-table links are derived from column naming conventions.
 */

/** How a column is filtered, edited and displayed — derived from its SQL type. */
export type ColumnKind =
  | 'text'
  | 'integer'
  | 'decimal'
  | 'float'
  | 'bool'
  | 'uuid'
  | 'datetime'
  | 'datetimeoffset'
  | 'date'
  | 'time'
  | 'binary'
  | 'other';

export interface AnalyticsColumn {
  /** Name used for sortBy/filters (from the schema). */
  name: string;
  /** Property name in JSON rows (System.Text.Json camelCase). */
  jsonKey: string;
  label: string;
  kind: ColumnKind;
  sqlType: string;
  isNullable: boolean;
  maxLength: number | null;
  precision: number | null;
  scale: number | null;
  /** Values of this column are IDs of rows in the referenced entity object. */
  ref?: AnalyticsObject;
}

export interface EntityRelation {
  /** Object whose rows reference the entity. */
  object: AnalyticsObject;
  /** Column holding the entity's ID. */
  column: AnalyticsColumn;
  label: string;
}

export interface EntityInfo {
  /** Singular noun, e.g. "Customer". */
  noun: string;
  idColumn: AnalyticsColumn;
  titleColumn?: AnalyticsColumn;
  relations: EntityRelation[];
}

export interface AnalyticsObject {
  /** `{schema}/{slug}` — also the app URL below `/analytics/`. */
  key: string;
  schema: string;
  name: string;
  slug: string;
  kind: 'table' | 'view';
  /** API path, e.g. `/api/analytics/stripe/charges`. */
  path: string;
  title: string;
  defaultSortColumns: string[];
  columns: AnalyticsColumn[];
  entity?: EntityInfo;
}

export interface SchemaGroup {
  schema: string;
  label: string;
  objects: AnalyticsObject[];
  /** Named sections inside the group, e.g. Events → "BI" (the bi schema's tables). */
  subgroups?: { label: string; objects: AnalyticsObject[] }[];
}

export interface AnalyticsModel {
  objects: AnalyticsObject[];
  groups: SchemaGroup[];
  find: (schema: string | undefined, slug: string | undefined) => AnalyticsObject | undefined;
  findGroup: (schema: string | undefined) => SchemaGroup | undefined;
}

// ---------- Naming ----------

const SCHEMA_LABELS: Record<string, string> = {
  api: 'Product',
  stripe: 'Stripe',
  dbo: 'OAA database',
  bi: 'Events',
  etl: 'ETL',
  biapp: 'BI app',
};

const SCHEMA_ORDER = ['api', 'stripe', 'dbo', 'bi', 'etl', 'biapp'];

export function schemaLabel(schema: string): string {
  return SCHEMA_LABELS[schema] ?? schema;
}

/** Words shown in upper case in labels. */
const ACRONYMS = new Set([
  'id',
  'json',
  'url',
  'pdf',
  'api',
  'utc',
  'vat',
  'os',
  'ip',
  'sso',
  'oaa',
]);

/** Brand names that must not be split at their inner capital. */
const COMPOUND_WORDS: Record<string, string> = { HubSpot: 'Hubspot' };

/**
 * Turns identifiers into labels: `WebClientUsage_30Days` / `web-client-usage-30-days` →
 * "Web client usage 30 days", `SubscriptionPlanId` → "Subscription plan ID",
 * `InvoicePdfUrl` → "Invoice PDF URL", `D365Id` → "D365 ID".
 */
export function humanize(identifier: string): string {
  const prepared = Object.entries(COMPOUND_WORDS).reduce(
    (text, [word, replacement]) => text.replaceAll(word, replacement),
    identifier,
  );
  const words = prepared
    .replace(/[-_]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([a-z])(\d)/g, '$1 $2')
    .replace(/(\d)([A-Za-z])/g, '$1 $2')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return words
    .map((word, index) => {
      const lower = word.toLowerCase();
      if (ACRONYMS.has(lower)) return lower.toUpperCase();
      if (/^[A-Z0-9]{2,}$/.test(word)) return word;
      return index === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    })
    .join(' ');
}

/** Port of System.Text.Json JsonNamingPolicy.CamelCase — the API's JSON property naming. */
export function toJsonKey(name: string): string {
  const isUpper = (char: string) =>
    char !== '' && char === char.toUpperCase() && char !== char.toLowerCase();
  if (!isUpper(name.charAt(0))) return name;
  // Lower-case the leading run of capitals, keeping the last one when a lower-case letter follows.
  let end = name.length;
  for (let i = 1; i < name.length; i++) {
    if (i === 1 && !isUpper(name.charAt(1))) {
      end = 1;
      break;
    }
    const next = name.charAt(i + 1);
    if (next !== '' && !isUpper(next)) {
      end = next === ' ' ? i + 1 : i;
      break;
    }
  }
  return name.slice(0, end).toLowerCase() + name.slice(end);
}

function singular(name: string): string {
  if (name.endsWith('ies')) return `${name.slice(0, -3)}y`;
  return name.endsWith('s') ? name.slice(0, -1) : name;
}

/** A label continued mid-sentence: "User ID" → "user ID"; a leading acronym stays ("OAA user ID"). */
function lowerFirst(label: string): string {
  return /^[A-Z0-9]{2}/.test(label) ? label : label.charAt(0).toLowerCase() + label.slice(1);
}

// ---------- Types ----------

const KIND_BY_SQL_TYPE: Record<string, ColumnKind> = {
  nvarchar: 'text',
  varchar: 'text',
  nchar: 'text',
  char: 'text',
  ntext: 'text',
  text: 'text',
  xml: 'text',
  sysname: 'text',
  tinyint: 'integer',
  smallint: 'integer',
  int: 'integer',
  bigint: 'integer',
  decimal: 'decimal',
  numeric: 'decimal',
  money: 'decimal',
  smallmoney: 'decimal',
  float: 'float',
  real: 'float',
  bit: 'bool',
  uniqueidentifier: 'uuid',
  datetime: 'datetime',
  datetime2: 'datetime',
  smalldatetime: 'datetime',
  datetimeoffset: 'datetimeoffset',
  date: 'date',
  time: 'time',
  binary: 'binary',
  varbinary: 'binary',
  image: 'binary',
  timestamp: 'binary',
  rowversion: 'binary',
};

export function columnKind(sqlType: string | null): ColumnKind {
  return KIND_BY_SQL_TYPE[(sqlType ?? '').toLowerCase()] ?? 'other';
}

// ---------- Entities and references ----------

/** Prefixes that qualify a reference, e.g. LatestInvoiceId → Invoice, DefaultPriceId → Price. */
const REFERENCE_QUALIFIERS = ['', 'Latest', 'Default', 'Failure'];

/** Preferred title columns for entity pages, in order. */
const TITLE_COLUMNS = ['name', 'displayname', 'email', 'normalizedemail', 'number', 'description'];

/** Groups shown as a named subgroup, by schema: Events → "BI" lists every bi table. */
const SUBGROUPS: Record<string, { label: string; tables?: string[] }[]> = {
  bi: [{ label: 'BI' }],
  // dbo holds copies of Open as App production tables next to the legacy app usage tables.
  dbo: [
    {
      label: 'Production',
      tables: ['Resources', 'SubscriptionPlans', 'Subscriptions', 'Teams', 'TeamUsers', 'Users'],
    },
    { label: 'Usage' },
  ],
};

/**
 * The most specific label of an object's group: its subgroup in a schema split into several
 * (dbo → "Production" or "Usage"), otherwise the schema's label (bi → "Events").
 */
export function objectGroupLabel(object: Pick<AnalyticsObject, 'schema' | 'name'>): string {
  const definitions = SUBGROUPS[object.schema];
  if (!definitions || definitions.length < 2) return schemaLabel(object.schema);
  const listed = definitions.find((d) => d.tables?.includes(object.name));
  return (listed ?? definitions.find((d) => !d.tables))?.label ?? schemaLabel(object.schema);
}

/** A schema's objects in its subgroups: listed tables first, the rest in the subgroup without a list. */
function subgroupsOf(schema: string, members: AnalyticsObject[]) {
  const definitions = SUBGROUPS[schema];
  if (!definitions) return undefined;
  const listed = new Set(definitions.flatMap((d) => d.tables ?? []));
  return definitions
    .map((d) => ({
      label: d.label,
      objects: members.filter((object) =>
        d.tables ? d.tables.includes(object.name) : !listed.has(object.name),
      ),
    }))
    .filter((subgroup) => subgroup.objects.length > 0);
}

/**
 * References the naming rule gets wrong, by `{schema}.{table}.{column}` (`*` = any table): the
 * target object key, or null for no link. In the bi event tables UserId is the app owner's Open as
 * App user ID and SubscriptionId the Open as App subscription (license) ID; both are keys of the
 * production copies (dbo.Users, dbo.Subscriptions), not of BI portal users or Stripe. Stripe's
 * OaaUserId / OaaSubscriptionId hold the same IDs.
 */
const REFERENCE_OVERRIDES: Record<string, string | null> = {
  'bi.*.UserId': 'dbo/users',
  'bi.*.SubscriptionId': 'dbo/subscriptions',
  'stripe.*.OaaUserId': 'dbo/users',
  'stripe.*.OaaSubscriptionId': 'dbo/subscriptions',
};

function findEntity(
  column: AnalyticsColumn,
  owner: AnalyticsObject,
  entitiesByNoun: Map<string, AnalyticsObject[]>,
  byKey: Map<string, AnalyticsObject>,
): AnalyticsObject | undefined {
  // An exact entry wins over a schema-wide `*` one, also when it is null (no link).
  const exact = `${owner.schema}.${owner.name}.${column.name}`;
  const wildcard = `${owner.schema}.*.${column.name}`;
  const key =
    exact in REFERENCE_OVERRIDES ? exact : wildcard in REFERENCE_OVERRIDES ? wildcard : null;
  if (key !== null) {
    const target = REFERENCE_OVERRIDES[key];
    return target ? byKey.get(target) : undefined;
  }
  for (const qualifier of REFERENCE_QUALIFIERS) {
    if (!column.name.startsWith(qualifier) || !column.name.endsWith('Id')) continue;
    const noun = column.name.slice(qualifier.length, -2);
    const candidates = entitiesByNoun.get(noun.toLowerCase());
    if (!candidates?.length) continue;
    // Same schema wins (stripe.SubscriptionId → stripe.Subscriptions); otherwise the only match.
    const sameSchema = candidates.find((candidate) => candidate.schema === owner.schema);
    if (sameSchema) return sameSchema;
    if (candidates.length === 1) return candidates[0];
  }
  return undefined;
}

// ---------- Build ----------

function toColumn(dto: AnalyticsSchemaColumnDto): AnalyticsColumn {
  return {
    name: dto.name,
    jsonKey: toJsonKey(dto.name),
    label: humanize(dto.name),
    kind: columnKind(dto.sqlType),
    sqlType: (dto.sqlType ?? '').toLowerCase(),
    isNullable: dto.isNullable,
    maxLength: dto.maxLength,
    precision: dto.precision,
    scale: dto.scale,
  };
}

/** Views named `vw_X` read as "X (view)", e.g. bi.vw_AppEvent → "App event (view)". */
const VIEW_PREFIX = /^vw_/i;

function objectTitle(name: string): string {
  return VIEW_PREFIX.test(name)
    ? `${humanize(name.replace(VIEW_PREFIX, ''))} (view)`
    : humanize(name);
}

function toObject(dto: AnalyticsSchemaObjectDto): AnalyticsObject {
  const slug = dto.path.split('/').filter(Boolean).pop() ?? dto.name.toLowerCase();
  return {
    key: `${dto.schema}/${slug}`,
    schema: dto.schema,
    name: dto.name,
    slug,
    kind: dto.kind.toUpperCase() === 'VIEW' ? 'view' : 'table',
    path: dto.path,
    title: objectTitle(dto.name),
    defaultSortColumns: dto.defaultSortColumns,
    columns: dto.columns.map(toColumn),
  };
}

/** Whether a `vw_X` view sits next to a table X of the same schema. */
function wrapsTable(view: AnalyticsObject, objects: AnalyticsObject[]): boolean {
  if (!VIEW_PREFIX.test(view.name)) return false;
  const table = view.name.replace(VIEW_PREFIX, '').toLowerCase();
  return objects.some(
    (o) => o.kind === 'table' && o.schema === view.schema && o.name.toLowerCase() === table,
  );
}

export function buildAnalyticsModel(schema: readonly AnalyticsSchemaObjectDto[]): AnalyticsModel {
  const objects = schema.map(toObject);

  // An entity is a table with an `Id` column, or keyed by `{Table}Id` alone (bi.EventType); it
  // gets a record page.
  const entitiesByNoun = new Map<string, AnalyticsObject[]>();
  const byKey = new Map(objects.map((object) => [object.key, object]));
  for (const object of objects) {
    const ownKey = `${singular(object.name)}Id`.toLowerCase();
    const idColumn =
      object.columns.find((column) => column.name.toLowerCase() === 'id') ??
      (object.defaultSortColumns.length === 1 &&
      object.defaultSortColumns[0]?.toLowerCase() === ownKey
        ? object.columns.find((column) => column.name.toLowerCase() === ownKey)
        : undefined);
    if (!idColumn || object.kind !== 'table') continue;
    const noun = singular(object.name);
    const titleColumn = TITLE_COLUMNS.map((name) =>
      object.columns.find((column) => column.name.toLowerCase() === name),
    ).find((column) => column !== undefined);
    object.entity = {
      noun: humanize(noun),
      idColumn,
      relations: [],
      ...(titleColumn ? { titleColumn } : {}),
    };
    idColumn.ref = object;
    const list = entitiesByNoun.get(noun.toLowerCase()) ?? [];
    list.push(object);
    entitiesByNoun.set(noun.toLowerCase(), list);
  }

  // `{Noun}Id` columns reference entities; the entity lists them as related records.
  for (const object of objects) {
    for (const column of object.columns) {
      if (column.ref) continue;
      const target = findEntity(column, object, entitiesByNoun, byKey);
      if (!target?.entity || target === object) continue;
      // A vw_X view that wraps table X adds no rows of its own: keep the table's relation only.
      if (object.kind === 'view' && wrapsTable(object, objects)) continue;
      column.ref = target;
      const noun = singular(target.name);
      target.entity.relations.push({
        object,
        column,
        label:
          column.name === `${noun}Id`
            ? object.title
            : // Oaa{Noun}Id is another system's copy of the ID: name the system ("Stripe invoices").
              column.name === `Oaa${noun}Id`
              ? `${schemaLabel(object.schema)} ${lowerFirst(object.title)}`
              : `${object.title} (${lowerFirst(column.label)})`,
      });
    }
  }

  const groups = [...new Set(objects.map((object) => object.schema))]
    .sort((a, b) => {
      const rank = (schemaName: string) =>
        SCHEMA_ORDER.includes(schemaName) ? SCHEMA_ORDER.indexOf(schemaName) : SCHEMA_ORDER.length;
      return rank(a) - rank(b) || a.localeCompare(b);
    })
    .map((schemaName) => {
      const members = objects.filter((object) => object.schema === schemaName);
      const subgroups = subgroupsOf(schemaName, members);
      return {
        schema: schemaName,
        label: schemaLabel(schemaName),
        objects: subgroups ? subgroups.flatMap((subgroup) => subgroup.objects) : members,
        ...(subgroups ? { subgroups } : {}),
      };
    });

  const groupsBySchema = new Map(groups.map((group) => [group.schema, group]));
  return {
    objects,
    groups,
    find: (schemaName, slug) =>
      schemaName && slug ? byKey.get(`${schemaName}/${slug}`) : undefined,
    findGroup: (schemaName) => (schemaName ? groupsBySchema.get(schemaName) : undefined),
  };
}

/** Finds a column by name, case-insensitively — the same rule the backend applies. */
export function findColumn(
  object: AnalyticsObject,
  name: string | null | undefined,
): AnalyticsColumn | undefined {
  if (!name) return undefined;
  const lower = name.toLowerCase();
  return object.columns.find((column) => column.name.toLowerCase() === lower);
}

/** Event logs that open newest first (their key starts with the event time). */
const NEWEST_FIRST = new Set([
  'bi/app-event',
  'bi/app-usage-daily',
  'bi/app-usage-monthly',
  'bi/preview-interaction-daily',
]);

/**
 * The time column event logs sort by, newest first (bi.AppEvent: EventTime); otherwise undefined,
 * and lists keep the API's default order.
 */
export function timeSortColumn(object: AnalyticsObject): string | undefined {
  if (!NEWEST_FIRST.has(object.key)) return undefined;
  return findColumn(object, object.defaultSortColumns[0])?.name;
}
